//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getClientCampaigns } from '@apis/campaigns/campaignsApi';
import { CampaignType } from '@apis/campaigns/campaignsApiModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import {
  ICampaignsListsState,
  NormalizedCampaign,
} from '@sampling/redux/campaigns-lists/campaignsListsModel';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import normalize from '@utils/normalize';

const initialState: ICampaignsListsState = {
  errors: {
    fetchInfluensterCampaignsError: undefined,
  },
  flags: {
    isFetchingInfluensterCampaigns: false,
  },

  [CampaignType.INFLUENSTER]: { byId: {}, orderedIds: [] },
};

const { actions, reducer: CampaignsListsReducer } = createSlice({
  name: SliceNames.CampaignsLists,
  initialState,
  reducers: {
    requestInfluensterCampaigns(state) {
      state.errors.fetchInfluensterCampaignsError = undefined;
      state.flags.isFetchingInfluensterCampaigns = true;
    },
    receiveInfluensterCampaignsSucceeded(
      state,
      { payload: campaigns }: PayloadAction<NormalizedCampaign>,
    ) {
      state[CampaignType.INFLUENSTER] = campaigns;
      state.flags.isFetchingInfluensterCampaigns = false;
    },
    receiveInfluensterCampaignsFailed(
      state,
      { payload: errorMsg }: PayloadAction<string>,
    ) {
      state.flags.isFetchingInfluensterCampaigns = false;
      state.errors.fetchInfluensterCampaignsError = errorMsg;
    },
  },
});

export const fetchInfluensterCampaigns = createThunk(() => async (dispatch) => {
  dispatch(actions.requestInfluensterCampaigns());

  try {
    const influensterCampaigns = await getClientCampaigns(
      CampaignType.INFLUENSTER,
      {},
    );

    dispatch(
      actions.receiveInfluensterCampaignsSucceeded(
        normalize(influensterCampaigns, (campaign) => campaign.campaignId),
      ),
    );
  } catch (e) {
    dispatch(
      actions.receiveInfluensterCampaignsFailed(
        getRequestErrorMessage(e) ?? '',
      ),
    );
  }
});

export default CampaignsListsReducer;
